import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "@lib/firebase";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

export const Shuttle = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedDepartureTime, setSelectedDepartureTime] = useState("");
  const [selectedPickupPoint, setSelectedPickupPoint] = useState("");
  const [selectedReturnTime, setSelectedReturnTime] = useState("");
  const [selectedReturnPoint, setSelectedReturnPoint] = useState("");
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSnapReady, setIsSnapReady] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [quotas, setQuotas] = useState({
    departure: {},
    return: {
      "Minggu, 28 September 2025 13:00 WIB": {
        booked: 0,
        available: 100,
        full: false,
      },
      "Minggu, 28 September 2025 19:00 WIB": {
        booked: 0,
        available: 70,
        full: false,
      },
    },
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const isMounted = useRef(true);
  const fetchStatus = useRef({ isFetching: false });

  const SHUTTLE_PRICE = 200000;
  const SHUTTLE_QUOTA = 70;
  const RETURN_QUOTA = {
    "Minggu, 28 September 2025 13:00 WIB": 100,
    "Minggu, 28 September 2025 19:00 WIB": 70,
  };
  const EVENT_ID = "JOTR";

  const pickupPoints = [
    {
      name: "Jakarta Selatan",
      meetingPoint: "Kantor Walikota Jakarta Selatan",
    },
    { name: "Jakarta Timur", meetingPoint: "Kantor Walikota Jakarta Timur" },
    { name: "Jakarta Pusat", meetingPoint: "Kantor Walikota Jakarta Pusat" },
    { name: "Jakarta Barat", meetingPoint: "Kantor Walikota Jakarta Barat" },
    { name: "Jakarta Utara", meetingPoint: "Kantor Walikota Jakarta Utara" },
  ];

  const departureTimes = [
    "Jumat, 26 September 2025 09:00 WIB",
    "Sabtu, 27 September 2025 09:00 WIB",
  ];

  const returnTimes = [
    "Minggu, 28 September 2025 13:00 WIB",
    "Minggu, 28 September 2025 19:00 WIB",
  ];

  const shuttleDetails = {
    kendaraan: "Hi Ace / Big Bravo / Big Alpha",
    harga: SHUTTLE_PRICE,
    images: ["/shuttle/01.png", "/shuttle/02.png"],
    terms:
      "Pembayaran non-refundable. Datang 30 menit sebelum keberangkatan. Bawa identitas diri.",
  };

  // Inisialisasi Midtrans Snap
  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);

    return () => clearInterval(checkSnap);
  }, []);

  // Fungsi untuk mengganti gambar
  const handleImageChange = (direction) => {
    setCurrentImageIndex((prev) => {
      let newIndex = direction === "next" ? prev + 1 : prev - 1;
      if (newIndex < 0) newIndex = 0;
      if (newIndex >= shuttleDetails.images.length)
        newIndex = shuttleDetails.images.length - 1;
      return newIndex;
    });
  };

  // Fetch kuota keberangkatan dan kepulangan
  const fetchQuotas = useCallback(async () => {
    try {
      const departureQuotaData = {};
      const returnQuotaData = {
        "Minggu, 28 September 2025 13:00 WIB": {
          booked: 0,
          available: RETURN_QUOTA["Minggu, 28 September 2025 13:00 WIB"],
          full: false,
        },
        "Minggu, 28 September 2025 19:00 WIB": {
          booked: 0,
          available: RETURN_QUOTA["Minggu, 28 September 2025 19:00 WIB"],
          full: false,
        },
      };

      // Kuota keberangkatan
      for (const point of pickupPoints) {
        departureQuotaData[point.name] = {};
        for (const time of departureTimes) {
          const q = query(
            collection(db, "pendaftaranShuttle"),
            where("pickupPoint", "==", point.name),
            where("departureTime", "==", time),
            where("transaction_status", "==", "settlement")
          );
          const snapshot = await getDocs(q);
          departureQuotaData[point.name][time] = {
            booked: snapshot.size,
            available: SHUTTLE_QUOTA - snapshot.size,
            full: snapshot.size >= SHUTTLE_QUOTA,
          };
        }
      }

      // Kuota kepulangan
      for (const time of returnTimes) {
        const q = query(
          collection(db, "pendaftaranShuttle"),
          where("returnTime", "==", time),
          where("transaction_status", "==", "settlement")
        );
        const snapshot = await getDocs(q);
        returnQuotaData[time] = {
          booked: snapshot.size,
          available: RETURN_QUOTA[time] - snapshot.size,
          full: snapshot.size >= RETURN_QUOTA[time],
        };
      }

      setQuotas({ departure: departureQuotaData, return: returnQuotaData });
    } catch (err) {
      console.error("[FETCH] Gagal fetch kuota:", err);
      showNotification("error", "Gagal memuat data kuota shuttle.");
    }
  }, [showNotification]);

  // Ambil data pengguna dan pemesanan shuttle
  const fetchUserDataAndPendingOrder = useCallback(async () => {
    if (!user?.uid || fetchStatus.current.isFetching) return;

    fetchStatus.current.isFetching = true;
    try {
      setInitialLoading(true);
      setError("");

      let userDataResult = null;
      let pendingOrderData = null;

      // Fetch user data
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        userDataResult = {
          ...userDoc.data(),
          email: userDoc.data().email || user.email,
        };
      } else {
        userDataResult = { email: user.email };
      }

      // Fetch existing shuttle registration
      const shuttleQuery = query(
        collection(db, "pendaftaranShuttle"),
        where("uid", "==", user.uid),
        where("eventId", "==", EVENT_ID),
        where("transaction_status", "in", ["pending", "settlement"])
      );
      const shuttleSnapshot = await getDocs(shuttleQuery);
      if (!shuttleSnapshot.empty) {
        const doc = shuttleSnapshot.docs[0];
        pendingOrderData = { id: doc.id, ...doc.data() };
      }

      if (isMounted.current) {
        setUserData(userDataResult);
        setPendingOrder(pendingOrderData);
      }
    } catch (err) {
      console.error("[ERROR] fetchUserDataAndPendingOrder:", {
        error: err.message,
        code: err.code,
      });
      if (isMounted.current) {
        setError(
          err.code === "permission-denied"
            ? "Akses ditolak. Anda tidak memiliki izin."
            : err.code === "unavailable"
            ? "Koneksi gagal. Periksa jaringan Anda."
            : "Gagal memuat data pengguna."
        );
        showNotification("error", "Gagal memuat data pengguna.");
      }
    } finally {
      if (isMounted.current) {
        setInitialLoading(false);
        setLoading(false);
      }
      fetchStatus.current.isFetching = false;
    }
  }, [user, showNotification]);

  // Efek awal
  useEffect(() => {
    isMounted.current = true;
    if (user?.uid) {
      fetchUserDataAndPendingOrder();
      fetchQuotas();
    } else {
      setUserData(null);
      setPendingOrder(null);
      setInitialLoading(false);
      setLoading(false);
    }

    return () => {
      isMounted.current = false;
    };
  }, [user, fetchUserDataAndPendingOrder, fetchQuotas]);

  // Handle simulasi pemesanan (untuk pengujian)
  const handleSimulateBooking = useCallback(async () => {
    if (initialLoading) {
      showNotification("warning", "Sedang memuat data, silakan tunggu.");
      return;
    }
    if (!user?.uid) {
      showNotification(
        "error",
        "Silakan login untuk melakukan simulasi pemesanan."
      );
      return;
    }
    if (pendingOrder) {
      showNotification("error", "Anda sudah memiliki pemesanan shuttle.");
      return;
    }
    if (quotas.departure[selectedPickupPoint]?.[selectedDepartureTime]?.full) {
      showNotification(
        "error",
        "Kuota keberangkatan untuk titik dan jadwal ini sudah penuh."
      );
      return;
    }
    if (quotas.return[selectedReturnTime]?.full) {
      showNotification(
        "error",
        "Kuota kepulangan untuk jadwal ini sudah penuh."
      );
      return;
    }
    if (
      !selectedDepartureTime ||
      !selectedPickupPoint ||
      !selectedReturnTime ||
      !selectedReturnPoint
    ) {
      showNotification(
        "error",
        "Silakan pilih jadwal keberangkatan, lokasi keberangkatan, jadwal kepulangan, dan lokasi kepulangan."
      );
      return;
    }
    if (
      !userData?.namaLengkap ||
      !userData?.telepon ||
      !userData?.alamat ||
      !userData?.gender ||
      !userData?.email
    ) {
      showNotification(
        "error",
        "Data pengguna tidak lengkap: Nama Lengkap, No HP, Alamat, Gender, dan Email harus diisi."
      );
      return;
    }

    setLoading(true);
    try {
      const orderId = `SIM-${Date.now()}`;

      const shuttleData = {
        uid: user.uid,
        pickupPoint: selectedPickupPoint,
        returnPoint: selectedReturnPoint,
        departureTime: selectedDepartureTime,
        returnTime: selectedReturnTime,
        harga: SHUTTLE_PRICE,
        eventId: EVENT_ID,
        status: "settlement",
        createdAt: new Date().toISOString(),
        email: userData.email,
        namaLengkap: userData.namaLengkap,
        telepon: userData.telepon,
        alamat: userData.alamat,
        gender: userData.gender,
      };

      // Simpan data ke Firebase
      await setDoc(doc(db, "pendaftaranShuttle", orderId), shuttleData);

      showNotification(
        "success",
        "Simulasi pemesanan berhasil! Data tersimpan di Firebase."
      );
      setShowConfirmModal(false);
      await fetchUserDataAndPendingOrder();
      await fetchQuotas();
      navigate(
        `/myregistration?order_id=${orderId}&transaction_status=settlement`
      );
    } catch (err) {
      console.error(
        "[ERROR] handleSimulateBooking: Failed to simulate booking",
        {
          error: err.message,
        }
      );
      showNotification(
        "error",
        "Gagal melakukan simulasi pemesanan: " + err.message
      );
      setError("Gagal melakukan simulasi pemesanan: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [
    user,
    showNotification,
    pendingOrder,
    quotas,
    selectedDepartureTime,
    selectedPickupPoint,
    selectedReturnTime,
    selectedReturnPoint,
    userData,
    navigate,
    fetchUserDataAndPendingOrder,
    fetchQuotas,
    initialLoading,
  ]);

  // Handle klik tombol dinamis
  const handleButtonClick = useCallback(() => {
    if (pendingOrder) {
      navigate("/myregistration");
    } else {
      if (initialLoading) {
        showNotification("warning", "Sedang memuat data, silakan tunggu.");
        return;
      }
      if (!user?.uid) {
        navigate("/login");
        showNotification("error", "Silakan login untuk memesan bus.");
        return;
      }
      if (!isSnapReady) {
        showNotification("error", "Midtrans sedang disiapkan, mohon tunggu.");
        return;
      }
      if (
        quotas.departure[selectedPickupPoint]?.[selectedDepartureTime]?.full
      ) {
        showNotification(
          "error",
          "Kuota keberangkatan untuk titik dan jadwal ini sudah penuh."
        );
        return;
      }
      if (quotas.return[selectedReturnTime]?.full) {
        showNotification(
          "error",
          "Kuota kepulangan untuk jadwal ini sudah penuh."
        );
        return;
      }
      if (
        !selectedDepartureTime ||
        !selectedPickupPoint ||
        !selectedReturnTime ||
        !selectedReturnPoint
      ) {
        showNotification(
          "error",
          "Silakan pilih jadwal keberangkatan, lokasi keberangkatan, jadwal kepulangan, dan lokasi kepulangan."
        );
        return;
      }
      if (
        !userData?.namaLengkap ||
        !userData?.telepon ||
        !userData?.alamat ||
        !userData?.gender ||
        !userData?.email
      ) {
        showNotification(
          "error",
          "Data pengguna tidak lengkap: Nama Lengkap, No HP, Alamat, Gender, dan Email harus diisi."
        );
        return;
      }

      setShowConfirmModal(true);
    }
  }, [
    user,
    navigate,
    showNotification,
    isSnapReady,
    pendingOrder,
    quotas,
    selectedDepartureTime,
    selectedPickupPoint,
    selectedReturnTime,
    selectedReturnPoint,
    userData,
    initialLoading,
  ]);

  // Handle konfirmasi pemesanan
  const handleConfirmBooking = useCallback(async () => {
    if (!selectedPickupPoint || !selectedReturnPoint) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/shuttle-register`,
        {
          uid: user.uid,
          pickupPoint: selectedPickupPoint,
          returnPoint: selectedReturnPoint,
          departureTime: selectedDepartureTime,
          returnTime: selectedReturnTime,
          harga: SHUTTLE_PRICE,
          eventId: EVENT_ID,
          namaLengkap: userData?.namaLengkap || "",
          telepon: userData?.telepon || "",
          alamat: userData?.alamat || "",
          gender: userData?.gender || "",
          email: userData?.email || user.email || "",
        }
      );

      const { snapToken, orderId } = response.data;
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Pemesanan shuttle berhasil!");
          setShowConfirmModal(false);
          await fetchUserDataAndPendingOrder();
          await fetchQuotas();
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=settlement`
          );
        },
        onPending: async () => {
          showNotification("warning", "Pembayaran shuttle belum selesai.");
          setShowConfirmModal(false);
          await fetchUserDataAndPendingOrder();
          await fetchQuotas();
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=pending`
          );
        },
        onError: () => {
          showNotification("error", "Pembayaran shuttle gagal.");
          setShowConfirmModal(false);
        },
        onClose: () => {
          setShowConfirmModal(false);
        },
      });
    } catch (err) {
      console.error("[ERROR] handleConfirmBooking: Failed to book shuttle", {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      const errorMessage = err.response?.data?.errors
        ? `Gagal memesan shuttle: ${err.response.data.errors.join(", ")}`
        : `Gagal memesan shuttle: ${
            err.response?.data?.message || err.message
          }`;
      showNotification("error", errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    user,
    selectedPickupPoint,
    selectedReturnPoint,
    selectedDepartureTime,
    selectedReturnTime,
    userData,
    showNotification,
    navigate,
    fetchUserDataAndPendingOrder,
    fetchQuotas,
  ]);

  return (
    <section className="min-h-screen bg-white py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl border border-gray-200 p-4 mb-6 shadow-none"
        >
          {initialLoading ? (
            <p className="text-lg text-gray-600 font-medium text-center">
              Memuat data pendaftaran...
            </p>
          ) : user && userData ? (
            <div className="text-center md:text-left space-y-3">
              <p className="text-base text-gray-600">
                {userData.namaLengkap || "Pengguna"}, silakan pilih jadwal dan
                lokasi keberangkatan serta kepulangan Anda dengan cermat. Tiket
                shuttle berlaku untuk perjalanan pulang-pergi. Tiket bersifat
                non-refundable.
              </p>

              {pendingOrder && (
                <p className="text-base text-lime-600 font-medium">
                  {pendingOrder.transaction_status === "pending"
                    ? "Pembayaran shuttle Anda belum selesai."
                    : "Anda sudah terdaftar untuk shuttle."}{" "}
                  Silakan cek status di{" "}
                  <a
                    href="/myregistration"
                    className="text-lime-600 hover:text-lime-800"
                  >
                    halaman pendaftaran
                  </a>
                  .
                </p>
              )}
            </div>
          ) : (
            <p className="text-lg text-gray-600 font-medium text-center md:text-left">
              Silakan login untuk memesan shuttle dan melihat status pendaftaran
              Anda.
            </p>
          )}
        </motion.div>

        {/* Notifikasi Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className="bg-red-50 text-red-700 p-4 rounded-xl border border-red-200 mb-6 flex items-center justify-center shadow-none"
            >
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="h-5 w-5 mr-3"
              />
              <span>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Formulir Pemesanan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl border border-gray-200 p-4 shadow-none"
        >
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Pemesanan Shuttle JOTR
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {shuttleDetails.kendaraan}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Jadwal Keberangkatan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Jadwal Keberangkatan
                  </label>
                  <div className="relative">
                    <select
                      className="w-full p-3 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-lime-600 focus:border-lime-600 transition-all appearance-none shadow-none disabled:bg-gray-200"
                      style={{ backgroundColor: "white", color: "#374151" }}
                      value={selectedDepartureTime}
                      onChange={(e) => setSelectedDepartureTime(e.target.value)}
                      disabled={initialLoading || pendingOrder}
                    >
                      <option
                        value=""
                        disabled
                        className="bg-white text-gray-700"
                      >
                        Pilih Jadwal
                      </option>
                      {departureTimes.map((time) => (
                        <option
                          key={time}
                          value={time}
                          disabled={
                            quotas.departure[selectedPickupPoint]?.[time]?.full
                          }
                          className="bg-white text-gray-700"
                          style={{ backgroundColor: "white", color: "#374151" }}
                        >
                          {time}{" "}
                          {quotas.departure[selectedPickupPoint]?.[time]?.full
                            ? "(Penuh)"
                            : ""}
                        </option>
                      ))}
                    </select>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
                    />
                  </div>
                </div>

                {/* Lokasi Keberangkatan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lokasi Keberangkatan
                  </label>
                  <div className="relative">
                    <select
                      className="w-full p-3 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-lime-600 focus:border-lime-600 transition-all appearance-none shadow-none disabled:bg-gray-200"
                      style={{ backgroundColor: "white", color: "#374151" }}
                      value={selectedPickupPoint}
                      onChange={(e) => setSelectedPickupPoint(e.target.value)}
                      disabled={initialLoading || pendingOrder}
                    >
                      <option
                        value=""
                        disabled
                        className="bg-white text-gray-700"
                      >
                        Pilih Lokasi
                      </option>
                      {pickupPoints.map((point) => (
                        <option
                          key={point.name}
                          value={point.name}
                          className="bg-white text-gray-700"
                          style={{ backgroundColor: "white", color: "#374151" }}
                        >
                          {point.meetingPoint}
                        </option>
                      ))}
                    </select>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
                    />
                  </div>
                </div>

                {/* Jadwal Kepulangan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Jadwal Kepulangan
                  </label>
                  <div className="relative">
                    <select
                      className="w-full p-3 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-lime-600 focus:border-lime-600 transition-all appearance-none shadow-none disabled:bg-gray-200"
                      style={{ backgroundColor: "white", color: "#374151" }}
                      value={selectedReturnTime}
                      onChange={(e) => setSelectedReturnTime(e.target.value)}
                      disabled={initialLoading || pendingOrder}
                    >
                      <option
                        value=""
                        disabled
                        className="bg-white text-gray-700"
                      >
                        Pilih Jadwal
                      </option>
                      {returnTimes.map((time) => (
                        <option
                          key={time}
                          value={time}
                          disabled={quotas.return[time]?.full}
                          className="bg-white text-gray-700"
                          style={{ backgroundColor: "white", color: "#374151" }}
                        >
                          {time} {quotas.return[time]?.full ? "(Penuh)" : ""}
                        </option>
                      ))}
                    </select>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
                    />
                  </div>
                </div>

                {/* Lokasi Kepulangan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lokasi Kepulangan
                  </label>
                  <div className="relative">
                    <select
                      className="w-full p-3 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-lime-600 focus:border-lime-600 transition-all appearance-none shadow-none disabled:bg-gray-200"
                      style={{ backgroundColor: "white", color: "#374151" }}
                      value={selectedReturnPoint}
                      onChange={(e) => setSelectedReturnPoint(e.target.value)}
                      disabled={initialLoading || pendingOrder}
                    >
                      <option
                        value=""
                        disabled
                        className="bg-white text-gray-700"
                      >
                        Pilih Lokasi
                      </option>
                      {pickupPoints.map((point) => (
                        <option
                          key={point.name}
                          value={point.name}
                          className="bg-white text-gray-700"
                          style={{ backgroundColor: "white", color: "#374151" }}
                        >
                          {point.meetingPoint}
                        </option>
                      ))}
                    </select>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Harga dan Tombol */}
            <div className="flex-1 flex flex-col">
              {/* Logo Bluebird */}
              <div className="mb-8 sm:mb-4 text-center sm:text-right">
                <img
                  src="/partners/bluebird.png"
                  alt="Bluebird Logo"
                  className="inline-block h-10 w-auto"
                />
              </div>

              {/* Spacer untuk mendorong harga & tombol ke bawah */}
              <div className="flex-1"></div>

              {/* Harga */}
              <p className="text-2xl font-bold text-lime-600 mb-4 text-center sm:text-right">
                Rp {shuttleDetails.harga.toLocaleString()}
                <span className="text-sm text-gray-500"></span>
              </p>

              {/* Tombol */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center sm:justify-end">
                <button
                  onClick={handleButtonClick}
                  className="w-full sm:w-48 py-3 rounded-lg text-sm font-semibold text-white bg-lime-600 hover:bg-lime-700 transition-all duration-200 shadow-none"
                >
                  {pendingOrder ? "Cek Pendaftaran" : "Pesan Sekarang"}
                </button>
              </div>
            </div>
          </div>

          {/* Tabel Kuota dan Tab Detail */}
          <div className="mt-8 flex flex-col gap-6">
            {/* Tabel Kuota Keberangkatan */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4">
                Kuota Keberangkatan
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-gray-600 border border-gray-200 rounded-lg shadow-none">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-4 text-left font-semibold text-gray-800">
                        Lokasi
                      </th>
                      {departureTimes.map((time) => (
                        <th
                          key={time}
                          className="p-4 text-left font-semibold text-gray-800"
                        >
                          {time}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pickupPoints.map((point) => (
                      <tr
                        key={point.name}
                        className="border-t border-gray-200 hover:bg-gray-50"
                      >
                        <td className="p-4">{point.meetingPoint}</td>
                        {departureTimes.map((time) => (
                          <td key={time} className="p-4">
                            {quotas.departure[point.name]?.[time]?.booked || 0}{" "}
                            / {SHUTTLE_QUOTA} kursi terisi
                            <br />
                            <span className="text-xs text-gray-500">
                              (
                              {quotas.departure[point.name]?.[time]
                                ?.available || SHUTTLE_QUOTA}{" "}
                              tersedia)
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Kuota Kepulangan */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4">
                Kuota Kepulangan
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {returnTimes.map((time) => (
                  <div
                    key={time}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200 shadow-none"
                  >
                    <p className="text-sm font-medium text-gray-800">{time}</p>
                    <p className="text-sm text-gray-600">
                      {quotas.return[time]?.booked || 0} / {RETURN_QUOTA[time]}{" "}
                      kursi terisi
                      <span className="text-xs text-gray-500">
                        {" "}
                        ({quotas.return[time]?.available ||
                          RETURN_QUOTA[time]}{" "}
                        tersedia)
                      </span>
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tab Detail */}
            <div className="flex justify-between items-center">
              <button
                onClick={() =>
                  setActiveTab(activeTab === "detail" ? null : "detail")
                }
                className={`text-base font-medium text-lime-600 hover:text-lime-700 transition-colors ${
                  activeTab === "detail" ? "underline underline-offset-4" : ""
                }`}
              >
                Detail Shuttle
              </button>
            </div>
          </div>

          {/* Konten Tab Detail */}
          {activeTab === "detail" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-6 border-t border-gray-200 pt-4"
            >
              <div className="flex flex-col md:flex-row gap-8">
                {/* Syarat dan Ketentuan */}
                <div className="flex-1 space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 text-lg">
                      Syarat dan Ketentuan
                    </h4>
                    <ul className="text-sm mt-4 text-gray-600 list-disc list-inside space-y-2">
                      {shuttleDetails.terms
                        .split(". ")
                        .filter((term) => term.trim())
                        .map((term, index) => (
                          <li key={index}>{term}</li>
                        ))}
                    </ul>
                  </div>
                </div>

                {/* Container Gambar */}
                <div className="flex-1 relative">
                  <img
                    src={shuttleDetails.images[currentImageIndex]}
                    alt={`Kendaraan ${currentImageIndex + 1}`}
                    className="w-full h-auto md:h-80 object-contain md:object-cover rounded-xl border border-gray-200 shadow-none"
                    onError={() =>
                      console.error(
                        "[ERROR] Failed to load image:",
                        shuttleDetails.images[currentImageIndex]
                      )
                    }
                  />

                  {/* Tombol navigasi gambar */}
                  {shuttleDetails.images.length > 1 && (
                    <div className="absolute inset-x-0 top-1/2 flex justify-between px-4">
                      <button
                        onClick={() => handleImageChange("prev")}
                        disabled={currentImageIndex === 0}
                        className={`bg-gray-800 bg-opacity-70 text-white w-10 h-10 flex items-center justify-center rounded-full hover:bg-opacity-90 transition-all shadow-none ${
                          currentImageIndex === 0
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        aria-label="Previous image"
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          className="text-base"
                        />
                      </button>

                      <button
                        onClick={() => handleImageChange("next")}
                        disabled={
                          currentImageIndex === shuttleDetails.images.length - 1
                        }
                        className={`bg-gray-800 bg-opacity-70 text-white w-10 h-10 flex items-center justify-center rounded-full hover:bg-opacity-90 transition-all shadow-none ${
                          currentImageIndex === shuttleDetails.images.length - 1
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        aria-label="Next image"
                      >
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="text-base"
                        />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Modal Konfirmasi Biodata */}
        <AnimatePresence>
          {showConfirmModal && userData && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-2xl border border-gray-200 p-4 max-w-lg w-full shadow-none"
              >
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                  Konfirmasi Pemesanan Shuttle
                </h3>
                <p className="text-gray-600 mb-6 text-center">
                  Pastikan data dan pilihan Anda benar sebelum melanjutkan.
                </p>
                <div className="mb-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <p className="text-sm">
                      <strong className="text-gray-800">Nama Lengkap:</strong>{" "}
                      {userData.namaLengkap || "Tidak tersedia"}
                    </p>
                    <p className="text-sm">
                      <strong className="text-gray-800">No HP:</strong>{" "}
                      {userData.telepon || "Tidak tersedia"}
                    </p>
                    <p className="text-sm">
                      <strong className="text-gray-800">Alamat:</strong>{" "}
                      {userData.alamat || "Tidak tersedia"}
                    </p>
                    <p className="text-sm">
                      <strong className="text-gray-800">Gender:</strong>{" "}
                      {userData.gender || "Tidak tersedia"}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <p className="text-sm">
                      <strong className="text-gray-800">Keberangkatan:</strong>{" "}
                      {selectedDepartureTime} dari {selectedPickupPoint}
                    </p>
                    <p className="text-sm">
                      <strong className="text-gray-800">Kepulangan:</strong>{" "}
                      {selectedReturnTime} ke {selectedReturnPoint}
                    </p>
                  </div>
                </div>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="px-6 py-3 rounded-lg text-sm font-medium bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors shadow-none"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleConfirmBooking}
                    className={`px-6 py-3 rounded-lg text-sm font-medium text-white transition-all shadow-none ${
                      loading
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-lime-600 hover:bg-lime-700"
                    }`}
                    disabled={loading}
                  >
                    {loading ? "Memproses..." : "Konfirmasi dan Bayar"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
